/**
 * Flutter SDK — operator UI (feature #185).
 *
 * Three stacked cards:
 *   - Summary: total / active / iOS / Android / cross-platform.
 *   - Apps table with dual iOS/Android platform badges, status, channel,
 *     config preview, row actions (edit / toggle active / delete).
 *   - Per-app config editor (JSON textarea with inline validation).
 *   - Cross-platform installations pane with platform filter chip.
 *
 * Data layer: TanStack Query. Pure helpers live in `./api`. Brand: emerald
 * -> sky headline gradient, zinc borders, 6-8px rounded, light theme only,
 * no emojis.
 */

import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createApp,
	createInstallation,
	deleteApp,
	describeAppPlatforms,
	fetchApps,
	fetchInstallations,
	flutterSdkQueryKeys,
	FLUTTER_CHANNELS,
	FLUTTER_PLATFORMS,
	formatChannelLabel,
	formatConfigPreview,
	formatInteger,
	formatPlatformLabel,
	formatTimestamp,
	hasAtLeastOnePlatform,
	isFlutterChannel,
	isFlutterPlatform,
	isValidAndroidPackage,
	isValidAppId,
	isValidAppName,
	isValidIosBundleId,
	isValidSemVer,
	parseConfigInput,
	sortApps,
	sortInstallations,
	summarizeApps,
	updateApp,
	type FlutterChannel,
	type FlutterPlatform,
	type FlutterSdkAppView,
} from "./api";

// ---------------------------------------------------------------------------
// Tailwind style tokens — same palette as the other tracking pages.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const TEXTAREA =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 font-mono text-xs text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_IOS =
	"inline-flex items-center rounded-[4px] border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-700";
const BADGE_ANDROID =
	"inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700";
const BADGE_NONE =
	"inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[11px] font-medium text-zinc-500";
const BADGE_STATUS_ACTIVE =
	"inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700";
const BADGE_STATUS_PAUSED =
	"inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[11px] font-medium text-zinc-600";
const HEADLINE =
	"bg-gradient-to-r from-emerald-600 to-sky-500 bg-clip-text text-transparent";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface FlutterSdkPageProps {
	initialAppId?: string;
	initialPlatform?: FlutterPlatform;
}

export function FlutterSdkPage(props: FlutterSdkPageProps = {}) {
	const queryClient = useQueryClient();

	// Create-app form state.
	const [appName, setAppName] = useState("");
	const [iosBundle, setIosBundle] = useState("");
	const [androidPkg, setAndroidPkg] = useState("");
	const [channel, setChannel] = useState<FlutterChannel>("stable");
	const [configText, setConfigText] = useState("{}");
	const [configError, setConfigError] = useState<string | null>(null);

	// Installations filter state.
	const [installFilterApp, setInstallFilterApp] = useState<string | null>(
		props.initialAppId && isValidAppId(props.initialAppId)
			? props.initialAppId
			: null,
	);
	const [platformFilter, setPlatformFilter] = useState<FlutterPlatform | "all">(
		props.initialPlatform && isFlutterPlatform(props.initialPlatform)
			? props.initialPlatform
			: "all",
	);

	// Per-app config editor state.
	const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
	const [editingConfigText, setEditingConfigText] = useState("");
	const [editingConfigError, setEditingConfigError] = useState<string | null>(
		null,
	);

	// Manual install harness state (operator can seed an install from the UI).
	const [installAppId, setInstallAppId] = useState("");
	const [installPlatform, setInstallPlatform] = useState<FlutterPlatform>(
		"ios",
	);
	const [installSdkVersion, setInstallSdkVersion] = useState("0.1.0");
	const [installFlutterVersion, setInstallFlutterVersion] = useState("3.19.0");
	const [installDeviceId, setInstallDeviceId] = useState("");

	// -----------------------------------------------------------------
	// Queries
	// -----------------------------------------------------------------
	const appsQuery = useQuery({
		queryKey: flutterSdkQueryKeys.apps(),
		queryFn: fetchApps,
	});

	const apps = useMemo(
		() => sortApps(appsQuery.data?.items ?? []),
		[appsQuery.data?.items],
	);
	const summary = useMemo(() => summarizeApps(apps), [apps]);

	const installationsQuery = useQuery({
		queryKey: flutterSdkQueryKeys.installations(installFilterApp),
		queryFn: () =>
			fetchInstallations({
				app_id: installFilterApp ?? undefined,
				limit: 100,
			}),
	});

	const installations = useMemo(() => {
		const rows = sortInstallations(installationsQuery.data?.items ?? []);
		if (platformFilter === "all") return rows;
		return rows.filter((r) => r.platform === platformFilter);
	}, [installationsQuery.data?.items, platformFilter]);

	// -----------------------------------------------------------------
	// Mutations
	// -----------------------------------------------------------------
	const createMutation = useMutation({
		mutationFn: createApp,
		onSuccess: () => {
			toast.success("Flutter app registered.");
			setAppName("");
			setIosBundle("");
			setAndroidPkg("");
			setConfigText("{}");
			setConfigError(null);
			queryClient.invalidateQueries({
				queryKey: flutterSdkQueryKeys.apps(),
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to register the app."),
	});

	const updateMutation = useMutation({
		mutationFn: (args: { id: string; patch: Parameters<typeof updateApp>[1] }) =>
			updateApp(args.id, args.patch),
		onSuccess: () => {
			toast.success("App updated.");
			queryClient.invalidateQueries({
				queryKey: flutterSdkQueryKeys.apps(),
			});
			setEditingConfigId(null);
			setEditingConfigError(null);
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to update the app."),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteApp(id),
		onSuccess: () => {
			toast.success("App removed.");
			queryClient.invalidateQueries({
				queryKey: flutterSdkQueryKeys.apps(),
			});
			queryClient.invalidateQueries({
				queryKey: flutterSdkQueryKeys.installations(installFilterApp),
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to delete the app."),
	});

	const installMutation = useMutation({
		mutationFn: createInstallation,
		onSuccess: () => {
			toast.success("Installation recorded.");
			setInstallDeviceId("");
			queryClient.invalidateQueries({
				queryKey: flutterSdkQueryKeys.installations(installFilterApp),
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to record installation."),
	});

	const busy =
		createMutation.isPending ||
		updateMutation.isPending ||
		deleteMutation.isPending ||
		installMutation.isPending;

	// -----------------------------------------------------------------
	// Handlers
	// -----------------------------------------------------------------
	const handleCreate = useCallback(() => {
		const trimmedName = appName.trim();
		if (!isValidAppName(trimmedName)) {
			toast.error("App name must be 1..160 bytes.");
			return;
		}
		const ios = iosBundle.trim();
		const andPkg = androidPkg.trim();
		if (!hasAtLeastOnePlatform(ios || null, andPkg || null)) {
			toast.error("Provide an iOS bundle id and/or an Android package.");
			return;
		}
		if (ios && !isValidIosBundleId(ios)) {
			toast.error("iOS bundle id must be reverse-DNS, e.g. com.acme.ios.");
			return;
		}
		if (andPkg && !isValidAndroidPackage(andPkg)) {
			toast.error("Android package must be reverse-DNS, e.g. com.acme.app.");
			return;
		}
		if (!isFlutterChannel(channel)) {
			toast.error("Channel must be stable, beta, or dev.");
			return;
		}
		const parsedConfig = parseConfigInput(configText);
		if (!parsedConfig.ok) {
			setConfigError(parsedConfig.error);
			toast.error(`Config ${parsedConfig.error.replace("_", " ")}.`);
			return;
		}
		setConfigError(null);
		createMutation.mutate({
			app_name: trimmedName,
			ios_bundle_id: ios || null,
			android_package: andPkg || null,
			flutter_channel: channel,
			config: parsedConfig.value,
			active: true,
		});
	}, [
		appName,
		iosBundle,
		androidPkg,
		channel,
		configText,
		createMutation,
	]);

	const handleToggleActive = useCallback(
		(row: FlutterSdkAppView) => {
			if (busy) return;
			updateMutation.mutate({
				id: row.id,
				patch: { active: !row.active },
			});
		},
		[busy, updateMutation],
	);

	const handleDelete = useCallback(
		(row: FlutterSdkAppView) => {
			if (busy) return;
			deleteMutation.mutate(row.id);
		},
		[busy, deleteMutation],
	);

	const handleChannelChange = useCallback(
		(row: FlutterSdkAppView, next: FlutterChannel) => {
			if (busy || next === row.flutter_channel) return;
			updateMutation.mutate({
				id: row.id,
				patch: { flutter_channel: next },
			});
		},
		[busy, updateMutation],
	);

	const handleOpenEditor = useCallback((row: FlutterSdkAppView) => {
		setEditingConfigId(row.id);
		setEditingConfigText(JSON.stringify(row.config, null, 2));
		setEditingConfigError(null);
	}, []);

	const handleSaveEditor = useCallback(() => {
		if (!editingConfigId) return;
		const parsed = parseConfigInput(editingConfigText);
		if (!parsed.ok) {
			setEditingConfigError(parsed.error);
			return;
		}
		setEditingConfigError(null);
		updateMutation.mutate({
			id: editingConfigId,
			patch: { config: parsed.value },
		});
	}, [editingConfigId, editingConfigText, updateMutation]);

	const handleRecordInstall = useCallback(() => {
		const targetApp = installAppId.trim();
		if (!isValidAppId(targetApp)) {
			toast.error("Pick a valid app id (fla_...).");
			return;
		}
		if (!isFlutterPlatform(installPlatform)) {
			toast.error("Pick iOS or Android.");
			return;
		}
		if (!isValidSemVer(installSdkVersion)) {
			toast.error("SDK version must be SemVer (x.y.z).");
			return;
		}
		if (!isValidSemVer(installFlutterVersion)) {
			toast.error("Flutter version must be SemVer (x.y.z).");
			return;
		}
		const device = installDeviceId.trim();
		if (device.length === 0) {
			toast.error("Install id is required.");
			return;
		}
		installMutation.mutate({
			app_id: targetApp,
			platform: installPlatform,
			sdk_version: installSdkVersion,
			flutter_version: installFlutterVersion,
			install_id: device,
		});
	}, [
		installAppId,
		installPlatform,
		installSdkVersion,
		installFlutterVersion,
		installDeviceId,
		installMutation,
	]);

	// -----------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------
	const appsError = appsQuery.error as Error | undefined;
	const installsError = installationsQuery.error as Error | undefined;

	return (
		<section className="flex flex-col gap-4">
			<header className="flex flex-col gap-1">
				<h1 className={`text-2xl font-semibold ${HEADLINE}`}>Flutter SDK</h1>
				<p className="text-sm text-zinc-600">
					Register cross-platform Flutter apps (iOS bundle + Android package),
					ship a config blob the Dart plugin fetches on launch, and track
					installations.
				</p>
			</header>

			{/* Summary strip */}
			<div className={`${CARD} grid grid-cols-2 gap-0 p-0 sm:grid-cols-5`}>
				{[
					{ label: "Total", value: formatInteger(summary.total), tone: "primary" as const },
					{ label: "Active", value: formatInteger(summary.active), tone: "primary" as const },
					{ label: "iOS", value: formatInteger(summary.ios), tone: "neutral" as const },
					{ label: "Android", value: formatInteger(summary.android), tone: "neutral" as const },
					{ label: "Cross-platform", value: formatInteger(summary.cross_platform), tone: "neutral" as const },
				].map((it, i) => (
					<div
						key={it.label}
						className={
							"flex flex-col gap-1 px-4 py-3 " +
							(i > 0 ? "border-t border-zinc-200 sm:border-l sm:border-t-0" : "")
						}
					>
						<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
							{it.label}
						</span>
						<span
							className={
								"text-base font-semibold " +
								(it.tone === "primary" ? "text-emerald-700" : "text-zinc-900")
							}
						>
							{it.value}
						</span>
					</div>
				))}
			</div>

			{/* Register app form */}
			<div className={`${CARD} flex flex-col gap-4 p-4`}>
				<h2 className="text-base font-semibold text-zinc-900">
					Register Flutter app
				</h2>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="fsa-name" className={LABEL}>
							App name
						</label>
						<input
							id="fsa-name"
							className={INPUT}
							value={appName}
							onChange={(e) => setAppName(e.target.value)}
							placeholder="Acme mobile"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="fsa-channel" className={LABEL}>
							Flutter channel
						</label>
						<select
							id="fsa-channel"
							className={INPUT}
							value={channel}
							onChange={(e) =>
								isFlutterChannel(e.target.value)
									? setChannel(e.target.value)
									: null
							}
						>
							{FLUTTER_CHANNELS.map((c) => (
								<option key={c} value={c}>
									{formatChannelLabel(c)}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="fsa-ios" className={LABEL}>
							iOS bundle id (optional)
						</label>
						<input
							id="fsa-ios"
							className={INPUT}
							value={iosBundle}
							onChange={(e) => setIosBundle(e.target.value)}
							placeholder="com.acme.ios"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="fsa-android" className={LABEL}>
							Android package (optional)
						</label>
						<input
							id="fsa-android"
							className={INPUT}
							value={androidPkg}
							onChange={(e) => setAndroidPkg(e.target.value)}
							placeholder="com.acme.android"
						/>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label htmlFor="fsa-config" className={LABEL}>
							Config (JSON object)
						</label>
						<textarea
							id="fsa-config"
							className={TEXTAREA}
							rows={4}
							value={configText}
							onChange={(e) => {
								setConfigText(e.target.value);
								setConfigError(null);
							}}
						/>
						{configError ? (
							<span className="text-xs text-red-600">{configError.replace("_", " ")}</span>
						) : (
							<span className="text-xs text-zinc-500">
								Shipped verbatim to the plugin. Must be a JSON object under 16 KiB.
							</span>
						)}
					</div>
				</div>
				<div className="flex justify-end">
					<button
						type="button"
						onClick={handleCreate}
						disabled={busy || !appName.trim()}
						className={BTN_PRIMARY}
					>
						{createMutation.isPending ? "Registering..." : "Register app"}
					</button>
				</div>
			</div>

			{/* Apps table */}
			<div className={`${CARD} overflow-hidden`}>
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<h2 className="text-base font-semibold text-zinc-900">Flutter apps</h2>
					<span className="text-xs text-zinc-500">
						{formatInteger(apps.length)} total
					</span>
				</div>
				{appsQuery.isLoading ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						Loading apps...
					</div>
				) : appsError ? (
					<div className="px-4 py-8 text-center text-sm text-red-600">
						{appsError.message || "Unable to load apps."}
					</div>
				) : apps.length === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						No Flutter apps yet. Register one above to get started.
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<tr>
									<th scope="col" className="px-4 py-2">App</th>
									<th scope="col" className="px-4 py-2">Platforms</th>
									<th scope="col" className="px-4 py-2">Channel</th>
									<th scope="col" className="px-4 py-2">Config</th>
									<th scope="col" className="px-4 py-2">Status</th>
									<th scope="col" className="px-4 py-2">Updated</th>
									<th scope="col" className="px-4 py-2 text-right">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{apps.map((row) => (
									<AppRow
										key={row.id}
										row={row}
										busy={busy}
										onToggle={() => handleToggleActive(row)}
										onDelete={() => handleDelete(row)}
										onChannelChange={(next) => handleChannelChange(row, next)}
										onOpenEditor={() => handleOpenEditor(row)}
										onFilterInstalls={() => {
											setInstallFilterApp(row.id);
											setInstallAppId(row.id);
										}}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Per-app config editor */}
			{editingConfigId ? (
				<div className={`${CARD} flex flex-col gap-4 p-4`}>
					<div className="flex items-center justify-between">
						<h2 className="text-base font-semibold text-zinc-900">
							Edit config for{" "}
							<span className="font-mono text-xs text-zinc-700">
								{editingConfigId}
							</span>
						</h2>
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={() => {
								setEditingConfigId(null);
								setEditingConfigError(null);
							}}
						>
							Close
						</button>
					</div>
					<textarea
						className={TEXTAREA}
						rows={12}
						value={editingConfigText}
						onChange={(e) => {
							setEditingConfigText(e.target.value);
							setEditingConfigError(null);
						}}
					/>
					{editingConfigError ? (
						<span className="text-xs text-red-600">
							{editingConfigError.replace("_", " ")}
						</span>
					) : (
						<span className="text-xs text-zinc-500">
							The JSON object is shipped verbatim via GET /config to the Dart plugin.
						</span>
					)}
					<div className="flex justify-end gap-2">
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={() => setEditingConfigText("{}")}
						>
							Clear
						</button>
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={handleSaveEditor}
							disabled={busy}
						>
							{updateMutation.isPending ? "Saving..." : "Save config"}
						</button>
					</div>
				</div>
			) : null}

			{/* Install harness */}
			<div className={`${CARD} flex flex-col gap-3 p-4`}>
				<h2 className="text-base font-semibold text-zinc-900">
					Record installation
				</h2>
				<p className="text-xs text-zinc-500">
					Normally the plugin calls POST /installations on first launch. Use
					this form to simulate an install during QA.
				</p>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label htmlFor="fsi-app" className={LABEL}>
							App id
						</label>
						<input
							id="fsi-app"
							className={INPUT}
							value={installAppId}
							onChange={(e) => setInstallAppId(e.target.value)}
							placeholder="fla_..."
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="fsi-platform" className={LABEL}>
							Platform
						</label>
						<select
							id="fsi-platform"
							className={INPUT}
							value={installPlatform}
							onChange={(e) =>
								isFlutterPlatform(e.target.value)
									? setInstallPlatform(e.target.value)
									: null
							}
						>
							{FLUTTER_PLATFORMS.map((p) => (
								<option key={p} value={p}>
									{formatPlatformLabel(p)}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="fsi-sdk" className={LABEL}>
							SDK version
						</label>
						<input
							id="fsi-sdk"
							className={INPUT}
							value={installSdkVersion}
							onChange={(e) => setInstallSdkVersion(e.target.value)}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="fsi-flutter" className={LABEL}>
							Flutter version
						</label>
						<input
							id="fsi-flutter"
							className={INPUT}
							value={installFlutterVersion}
							onChange={(e) => setInstallFlutterVersion(e.target.value)}
						/>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-3">
						<label htmlFor="fsi-device" className={LABEL}>
							Install / device id
						</label>
						<input
							id="fsi-device"
							className={INPUT}
							value={installDeviceId}
							onChange={(e) => setInstallDeviceId(e.target.value)}
							placeholder="device-42"
						/>
					</div>
				</div>
				<div className="flex justify-end">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={handleRecordInstall}
						disabled={busy}
					>
						{installMutation.isPending ? "Recording..." : "Record install"}
					</button>
				</div>
			</div>

			{/* Installations list */}
			<div className={`${CARD} overflow-hidden`}>
				<div className="flex flex-col gap-2 border-b border-zinc-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Installations</h2>
					<div className="flex flex-wrap items-center gap-2">
						<label htmlFor="fsi-filter-app" className={LABEL}>
							App
						</label>
						<select
							id="fsi-filter-app"
							className={INPUT}
							value={installFilterApp ?? "__all__"}
							onChange={(e) =>
								setInstallFilterApp(
									e.target.value === "__all__" ? null : e.target.value,
								)
							}
						>
							<option value="__all__">All apps</option>
							{apps.map((a) => (
								<option key={a.id} value={a.id}>
									{a.app_name} ({a.id})
								</option>
							))}
						</select>
						<div className="flex items-center gap-1" role="group" aria-label="Platform filter">
							{(["all", "ios", "android"] as const).map((p) => (
								<button
									key={p}
									type="button"
									onClick={() => setPlatformFilter(p)}
									className={
										(platformFilter === p ? "bg-emerald-600 text-white border-emerald-600 " : "bg-white text-zinc-700 border-zinc-300 ") +
										"rounded-[6px] border px-2 py-1 text-xs font-medium hover:opacity-90"
									}
								>
									{p === "all" ? "All" : formatPlatformLabel(p)}
								</button>
							))}
						</div>
					</div>
				</div>
				{installationsQuery.isLoading ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						Loading installations...
					</div>
				) : installsError ? (
					<div className="px-4 py-8 text-center text-sm text-red-600">
						{installsError.message || "Unable to load installations."}
					</div>
				) : installations.length === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						No installations match the current filter.
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<tr>
									<th scope="col" className="px-4 py-2">Install id</th>
									<th scope="col" className="px-4 py-2">App</th>
									<th scope="col" className="px-4 py-2">Platform</th>
									<th scope="col" className="px-4 py-2">SDK</th>
									<th scope="col" className="px-4 py-2">Flutter</th>
									<th scope="col" className="px-4 py-2">Received</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{installations.map((row) => (
									<tr key={row.id}>
										<td className="px-4 py-2 font-mono text-xs text-zinc-700">
											{row.install_id}
										</td>
										<td className="px-4 py-2 text-zinc-900">
											<span className="font-mono text-xs">{row.app_id}</span>
										</td>
										<td className="px-4 py-2">
											<span
												className={
													row.platform === "ios" ? BADGE_IOS : BADGE_ANDROID
												}
											>
												{formatPlatformLabel(row.platform)}
											</span>
										</td>
										<td className="px-4 py-2 font-mono text-xs text-zinc-700">
											{row.sdk_version}
										</td>
										<td className="px-4 py-2 font-mono text-xs text-zinc-700">
											{row.flutter_version}
										</td>
										<td className="px-4 py-2 text-zinc-700">
											{formatTimestamp(row.created_at)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Row sub-component
// ---------------------------------------------------------------------------

interface AppRowProps {
	row: FlutterSdkAppView;
	busy: boolean;
	onToggle: () => void;
	onDelete: () => void;
	onChannelChange: (next: FlutterChannel) => void;
	onOpenEditor: () => void;
	onFilterInstalls: () => void;
}

function AppRow({
	row,
	busy,
	onToggle,
	onDelete,
	onChannelChange,
	onOpenEditor,
	onFilterInstalls,
}: AppRowProps) {
	const platforms = describeAppPlatforms(row);
	return (
		<tr>
			<td className="px-4 py-2 text-zinc-900">
				<div className="flex flex-col">
					<span className="font-medium">{row.app_name}</span>
					<span className="font-mono text-[11px] text-zinc-500">{row.id}</span>
				</div>
			</td>
			<td className="px-4 py-2">
				<div className="flex flex-wrap items-center gap-1">
					{row.ios_bundle_id ? (
						<span className={BADGE_IOS} title={row.ios_bundle_id}>
							iOS
						</span>
					) : null}
					{row.android_package ? (
						<span className={BADGE_ANDROID} title={row.android_package}>
							Android
						</span>
					) : null}
					{platforms === "none" ? (
						<span className={BADGE_NONE}>None</span>
					) : null}
				</div>
			</td>
			<td className="px-4 py-2">
				<select
					className={INPUT + " py-1"}
					value={row.flutter_channel}
					disabled={busy}
					onChange={(e) =>
						isFlutterChannel(e.target.value)
							? onChannelChange(e.target.value)
							: null
					}
				>
					{FLUTTER_CHANNELS.map((c) => (
						<option key={c} value={c}>
							{formatChannelLabel(c)}
						</option>
					))}
				</select>
			</td>
			<td className="px-4 py-2 text-zinc-700">
				<code className="text-xs text-zinc-600">
					{formatConfigPreview(row.config)}
				</code>
			</td>
			<td className="px-4 py-2">
				<span
					className={
						row.active ? BADGE_STATUS_ACTIVE : BADGE_STATUS_PAUSED
					}
				>
					{row.active ? "Active" : "Paused"}
				</span>
			</td>
			<td className="px-4 py-2 text-zinc-700">
				{formatTimestamp(row.updated_at)}
			</td>
			<td className="px-4 py-2 text-right">
				<div className="flex justify-end gap-2">
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={onFilterInstalls}
						disabled={busy}
					>
						Installs
					</button>
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={onOpenEditor}
						disabled={busy}
					>
						Edit config
					</button>
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={onToggle}
						disabled={busy}
					>
						{row.active ? "Pause" : "Activate"}
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={onDelete}
						disabled={busy}
					>
						Delete
					</button>
				</div>
			</td>
		</tr>
	);
}

export default FlutterSdkPage;
